import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { Order, Note } from '../types';
import { StatusBadge } from './StatusBadge';
export interface OrdersTableProps {
  orders: Order[];
  notes?: Note[];
}
type SortField = 'createdAt' | 'loanNumber' | 'orderType' | 'status' | 'assignedTo';
type SortDirection = 'asc' | 'desc';
export function OrdersTable({
  orders,
  notes = []
}: OrdersTableProps) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const sortedOrders = [...orders].sort((a, b) => {
    let aVal: any = a[sortField];
    let bVal: any = b[sortField];
    if (sortField === 'createdAt') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatOrderType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };
  const getLatestNote = (orderId: string) => {
    const orderNotes = notes.filter(n => n.orderId === orderId).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return orderNotes[0] || null;
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4" data-id="element-266" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-267" />;
  };
  if (orders.length === 0) {
    return <div className="bg-white border border-slate-100 rounded-2xl p-16 text-center" data-id="element-268">
        <p className="text-slate-600 mb-2 text-lg" data-id="element-269">No orders found</p>
        <p className="text-sm text-slate-400" data-id="element-270">
          Try adjusting your filters or create a new order
        </p>
      </div>;
  }
  return <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-id="element-271">
      <div className="overflow-x-auto" data-id="element-272">
        <table className="w-full" data-id="element-273">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-274">
            <tr data-id="element-275">
              <th onClick={() => handleSort('loanNumber')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-276">
                <div className="flex items-center space-x-1" data-id="element-277">
                  <span data-id="element-278">Loan</span>
                  <SortIcon field="loanNumber" data-id="element-279" />
                </div>
              </th>
              <th onClick={() => handleSort('orderType')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-280">
                <div className="flex items-center space-x-1" data-id="element-281">
                  <span data-id="element-282">Type</span>
                  <SortIcon field="orderType" data-id="element-283" />
                </div>
              </th>
              <th onClick={() => handleSort('status')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-284">
                <div className="flex items-center space-x-1" data-id="element-285">
                  <span data-id="element-286">Status</span>
                  <SortIcon field="status" data-id="element-287" />
                </div>
              </th>
              <th onClick={() => handleSort('assignedTo')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-288">
                <div className="flex items-center space-x-1" data-id="element-289">
                  <span data-id="element-290">Assigned To</span>
                  <SortIcon field="assignedTo" data-id="element-291" />
                </div>
              </th>
              <th onClick={() => handleSort('createdAt')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-292">
                <div className="flex items-center space-x-1" data-id="element-293">
                  <span data-id="element-294">Created</span>
                  <SortIcon field="createdAt" data-id="element-295" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-296">
                <span data-id="element-297">Recent Note</span>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-298">
                <span data-id="element-299">Note Date</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200" data-id="element-300">
            {sortedOrders.map(order => <tr key={order.id} onClick={() => navigate(`/orders/${order.id}`)} className="cursor-pointer transition-colors" style={{
            ':hover': {
              backgroundColor: '#FEF2F2'
            }
          }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FEF2F2'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'} data-id="element-301">
                <td className="px-6 py-3 whitespace-nowrap" data-id="element-302">
                  <button type="button" onClick={e => {
                e.stopPropagation();
                navigate(`/loans/${order.loanId}`);
              }} className="text-left group" data-id="element-303">
                    <div className="text-sm font-semibold underline-offset-2 group-hover:underline transition-colors" style={{
                  color: 'var(--nmhl-burgundy)'
                }} data-id="element-304">
                      {order.loanName || order.loanNumber}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-305">
                      {order.loanNumber}
                    </div>
                  </button>
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-600" data-id="element-306">
                  {formatOrderType(order.orderType)}
                </td>
                <td className="px-6 py-3 whitespace-nowrap" data-id="element-307">
                  <StatusBadge status={order.status} data-id="element-308" />
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-600" data-id="element-309">
                  {order.assignedTo}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-400" data-id="element-310">
                  {formatDate(order.createdAt)}
                </td>
                <td className="px-6 py-3 text-sm text-slate-600 max-w-[220px] truncate" data-id="element-311">
                  {getLatestNote(order.id)?.content || <span className="text-slate-300 italic" data-id="element-312">No notes</span>}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-400" data-id="element-313">
                  {getLatestNote(order.id) ? formatDate(getLatestNote(order.id)!.createdAt) : '—'}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}